import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@growdash/design-system/atoms';
import {
  CreditCardDto,
  CreditCardDtoNetworkEnum,
} from '@growdash/identity/generated';
import { ListChecks, Trash2 } from 'lucide-react';
import americanExpress from '../../../assets/credit-card-networks/american_express.png';
import amex from '../../../assets/credit-card-networks/amex.png';
import maestro from '../../../assets/credit-card-networks/maestro.png';
import mastercard from '../../../assets/credit-card-networks/mastercard.png';
import stripe from '../../../assets/credit-card-networks/stripe.png';
import visa from '../../../assets/credit-card-networks/visa.png';

interface ManagePaymentMethodsDialogProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  cards: CreditCardDto[];
  onSetDefault: (id: number) => Promise<void>;
  onRemove: (id: number) => Promise<void>;
  onAddCard: () => void; // To trigger the existing add card flow
  isLoading: boolean;
}

export function ManagePaymentMethodsDialog({
  isOpen,
  setIsOpen,
  cards,
  onSetDefault,
  onRemove,
  onAddCard,
  isLoading,
}: ManagePaymentMethodsDialogProps) {
  const cardNetworkImage = (network: CreditCardDtoNetworkEnum) => {
    switch (network) {
      case CreditCardDtoNetworkEnum.Visa:
        return visa;
      case CreditCardDtoNetworkEnum.Mastercard:
        return mastercard;
      case CreditCardDtoNetworkEnum.AmericanExpress:
        return americanExpress;
      case CreditCardDtoNetworkEnum.Amex:
        return amex;
      case CreditCardDtoNetworkEnum.Maestro:
        return maestro;
      default:
        return stripe;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Manage Payment Methods</DialogTitle>
          <DialogDescription>
            Set your preferred payment method or remove existing ones.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4 overflow-y-auto max-h-[calc(100vh-20rem)]">
          {cards.length === 0 && (
            <p className="text-sm text-muted-foreground">
              You have no payment methods saved.
            </p>
          )}
          {cards.map((card) => (
            <div
              key={card.cardToken}
              className="flex items-center justify-between p-3 border rounded-md"
            >
              <div>
                <p className="font-medium flex items-center">
                  <img
                    src={card.network ? cardNetworkImage(card.network) : stripe}
                    alt={card.network}
                    className="w-6 h-4 mr-2"
                  />{' '}
                  **** **** **** {card.lastFourDigits}
                </p>
                <p className="text-sm text-muted-foreground">{card.alias}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onSetDefault(card.id)}
                  disabled={card.isDefault || isLoading}
                  aria-label="Set as default"
                >
                  <ListChecks className="h-4 w-4 mr-2" />
                  {card.isDefault ? 'Default' : 'Set Default'}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => onRemove(card.id)}
                  disabled={card.isDefault || isLoading}
                  aria-label="Remove card"
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
        <DialogFooter className="sm:justify-between">
          <Button variant="outline" onClick={onAddCard}>
            Add New Card
          </Button>
          <Button variant="ghost" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
